// lib/client/pendingVisits.js
'use client';

const COOKIE_NAME = 'poh_pending_visits';
const MAX_PENDING = 50;
const MAX_AGE_SECONDS = 60 * 60 * 24 * 7; // 7 days

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getCookieValue(name) {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
  if (!parts) return null;
  return decodeURIComponent(parts.split('=').slice(1).join('='));
}

function setCookieValue(name, value, maxAge = MAX_AGE_SECONDS) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

export function readPendingVisits() {
  const raw = getCookieValue(COOKIE_NAME);
  if (!raw) return [];
  const parsed = safeParse(raw);
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry) => ({
      heartId: Number(entry?.heartId),
      seenAt: typeof entry?.seenAt === 'string' ? entry.seenAt : null,
    }))
    .filter((entry) => entry.heartId && entry.seenAt);
}

export function writePendingVisits(entries) {
  const sanitized = Array.isArray(entries) ? entries.slice(0, MAX_PENDING) : [];
  setCookieValue(COOKIE_NAME, JSON.stringify(sanitized));
}

export function addPendingVisit(heartId, seenAt) {
  const entries = readPendingVisits();
  const id = Number(heartId);
  if (!id || !seenAt) return;

  const existing = entries.find((entry) => entry.heartId === id);
  if (existing) {
    const existingTime = Date.parse(existing.seenAt);
    const incomingTime = Date.parse(seenAt);
    if (
      Number.isNaN(existingTime) ||
      (!Number.isNaN(incomingTime) && incomingTime < existingTime)
    ) {
      existing.seenAt = seenAt;
    }
    writePendingVisits(entries);
    return;
  }

  const next = [{ heartId: id, seenAt }, ...entries].slice(0, MAX_PENDING);
  writePendingVisits(next);
}

export function clearPendingVisits() {
  setCookieValue(COOKIE_NAME, '', 0);
}

export async function reconcilePendingVisits() {
  const entries = readPendingVisits();
  if (!entries.length) return;

  const [{ fetchJson }, { isVisited, markVisited, updateSyncTime }, { addToQueue }] =
    await Promise.all([
      import('./fetchJson'),
      import('./interactionCache'),
      import('./offlineQueue'),
    ]);

  await Promise.all(
    entries.map(async (entry) => {
      const { heartId, seenAt } = entry;
      if (!heartId || !seenAt) return;
      if (isVisited(heartId)) return;

      try {
        await fetchJson(`/api/hearts/${heartId}/visit`, {
          method: 'POST',
          body: { timestamp: seenAt },
        });
        markVisited(heartId, seenAt);
      } catch (error) {
        markVisited(heartId, seenAt);
        if (!navigator.onLine || error.status >= 500) {
          addToQueue({ type: 'visit', heartId, timestamp: seenAt });
        }
      }
    })
  );

  updateSyncTime();
  clearPendingVisits();
}

const CLICK_KEY_PREFIX = 'poh_checkin_click_at_';

export function setCheckinClickTimestamp(heartId, timestamp) {
  if (typeof sessionStorage === 'undefined') return;
  const id = Number(heartId);
  if (!id || !timestamp) return;
  sessionStorage.setItem(`${CLICK_KEY_PREFIX}${id}`, timestamp);
}

export function consumeCheckinClickTimestamp(heartId) {
  if (typeof sessionStorage === 'undefined') return null;
  const id = Number(heartId);
  if (!id) return null;
  const key = `${CLICK_KEY_PREFIX}${id}`;
  const value = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  return value || null;
}
